import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
    smsMessagesList: {
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        flexShrink: 1,
        maxWidth: '100%',
        minWidth: 0,
        overflowX: 'hidden',
        overflowY: 'hidden',
        position: 'relative',
        zIndex: 0,
        width: '-webkit-fill-available',
    },
    smsMessagesTop: {
        alignItems: 'center',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'row',
        flexShrink: '0',
        flexWrap: 'nowrap',
        justifyContent: 'space-between',
        gap: 8,
        minHeight: 60,
        padding: '8px 10px 8px 16px',
        position: 'relative',
        zIndex: '0',
        borderStyle: 'hidden hidden solid hidden',
        borderWidth: 'thin',
        borderBottomColor: theme.palette.divider,
        backgroundColor: theme.palette.background.paper,
        // Room for the tab of the conversation list at the left edge.
        [theme.breakpoints.down('md')]: {
            paddingLeft: 36,
        },
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        flexShrink: 0,
    },
    imageNameDivHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minWidth: 0,
    },
    headerText: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    headerButton: {
        color: theme.palette.text.secondary,
        '&:hover': {
            color: theme.palette.primary.light,
        },
    },
    headerName: {
        fontSize: 15.5,
        fontWeight: 500,
        lineHeight: 1.35,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    headerSubtitle: {
        fontSize: 12,
        lineHeight: 1.4,
        fontVariantNumeric: 'tabular-nums',
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    smsMessagesListContainer: {
         padding: '6px 20px 16px',
         backgroundColor: theme.palette.background.subtle,
         display: 'flex',
         flexDirection: 'column',
         overflowY: 'auto',
         overflowX: 'hidden',
         position: 'relative',
         width: '-webkit-fill-available',
         flex: 1,
    },
    noConversation: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        padding: 24,
        textAlign: 'center',
    },
    noConversationIcon: {
        width: 64,
        height: 64,
        marginBottom: 8,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.light,
        backgroundColor: 'rgba(77, 171, 245, 0.14)',
        '& svg': {
            fontSize: 30,
        },
    },
    noConversationTitle: {
        fontSize: 16,
        fontWeight: 500,
        color: theme.palette.text.primary,
    },
    noConversationText: {
        fontSize: 13.5,
        color: theme.palette.text.secondary,
    },
    // At the top of a long thread: brings in the page of messages before it.
    loadEarlier: {
        alignSelf: 'center',
        flexShrink: 0,
        margin: '10px 0 4px',
        padding: '5px 14px',
        borderRadius: 16,
        border: `1px solid ${theme.palette.divider}`,
        fontFamily: 'inherit',
        fontSize: 12.5,
        fontWeight: 500,
        color: theme.palette.primary.light,
        background: theme.palette.background.paper,
        cursor: 'pointer',
        '&:hover': {
            background: theme.palette.action.hover,
        },
        '&:disabled': {
            color: theme.palette.text.disabled,
            cursor: 'default',
        },
    },
    jumpPill: {
        position: 'absolute',
        left: '50%',
        bottom: 84,
        transform: 'translateX(-50%)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '6px 14px',
        borderRadius: 18,
        border: 'none',
        fontSize: 13,
        fontWeight: 500,
        color: '#fff',
        background: theme.palette.primary.light,
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.25)',
        cursor: 'pointer',
        zIndex: 2,
    },
    dropOverlay: {
        position: 'absolute',
        inset: 8,
        zIndex: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 12,
        border: `2px dashed ${theme.palette.primary.light}`,
        background: theme.palette.mode === 'dark' ? 'rgba(31, 34, 39, 0.9)' : 'rgba(255, 255, 255, 0.85)',
        color: theme.palette.primary.light,
        fontSize: 18,
        fontWeight: 500,
        pointerEvents: 'none',
    },
    smsCompose: {
        margin: '0px',
        padding: '10px 4px',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        bottom: 0,
        borderStyle: 'solid hidden hidden hidden',
        borderWidth: 'thin',
        borderTopColor: theme.palette.action.focus,
        backgroundColor: theme.palette.background.paper,
    },
    attachmentRow: {
        display: 'flex',
        padding: '0px 16px 8px 56px',
    },
    attachmentCard: {
        position: 'relative',
        display: 'inline-block',
    },
    attachmentThumb: {
        width: 72,
        height: 72,
        objectFit: 'cover',
        borderRadius: 10,
        display: 'block',
        boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.25)',
    },
    attachmentRemove: {
        position: 'absolute',
        top: -8,
        right: -8,
        background: theme.palette.background.paper,
        color: theme.palette.text.secondary,
        boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.3)',
        '&:hover': {
            background: theme.palette.background.paper,
            color: theme.palette.secondary.main,
        },
    },
    smsComposeBox: {
        display: 'flex',
        alignItems: 'flex-end',
        width: '100%',
        position: 'relative',
    },
    composeIcon: {
        padding: 8,
        margin: '0px 0px 0px 2px',
        color: theme.palette.text.secondary,
        '&:hover': {
            color: theme.palette.primary.light,
        },
        '& svg': {
            fontSize: 22,
        },
    },
    smsComposeInput: {
        flex: 1,
        display: 'flex',
        alignItems: 'flex-end',
        fontSize: 14.5,
        padding: '8px 14px',
        margin: '0px 6px',
        borderRadius: 20,
        border: '1px solid transparent',
        backgroundColor: theme.palette.action.hover,
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        '&.Mui-focused': {
            borderColor: theme.palette.primary.light,
            backgroundColor: theme.palette.background.paper,
        },
        '& textarea::placeholder': {
            fontSize: 13.5,
        },
        '& textarea': {
            lineHeight: 1.4,
        },
    },
    composeEnd: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginRight: 8,
    },
    smsCharacterCounter: {
        fontSize: 11,
        color: theme.palette.text.secondary,
        margin: '0px 0px 2px 0px',
        whiteSpace: 'nowrap',
    },
    smsCharacterCounterWarn: {
        color: theme.palette.secondary.main,
    },
    sendButton: {
        padding: 8,
        color: theme.palette.text.disabled,
        '& svg': {
            fontSize: 20,
        },
        transition: 'background-color 0.15s ease, color 0.15s ease',
    },
    sendButtonEnabled: {
        color: '#fff',
        backgroundColor: theme.palette.primary.light,
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
            filter: 'brightness(0.95)',
        },
    },
    newMessagePhoneContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minWidth: 0,
    },
    newMessagePhoneLabel: {
        fontSize: 14,
        color: theme.palette.text.secondary,
    },
    newMessagePhone: {
        minWidth: 200,
        '& input': {
            padding: '6px 0px',
            fontSize: 15.5,
            fontWeight: 500,
            fontVariantNumeric: 'tabular-nums',
        },
        '& input::placeholder': {
            fontSize: 13.5,
            fontWeight: 400,
        },
    },
}));

export { useStyles }
